import { useState } from "react";
import {
  BookOpen,
  FileText,
  Youtube,
  Facebook,
  Music,
  Video,
  ExternalLink,
  FileType,
  ChevronDown,
  X,
} from "lucide-react";
import { wisdomCapsules } from "@/data/wisdomCapsules";

/* Map each media type to an icon */
const TYPE_ICON = {
  html: FileText,
  pdf: FileType,
  youtube: Youtube,
  facebook: Facebook,
  audio: Music,
  video: Video,
  external: ExternalLink,
};

const TYPE_LABEL = {
  html: "Read",
  pdf: "Read PDF",
  youtube: "Watch",
  facebook: "Open",
  audio: "Listen",
  video: "Watch",
  external: "Open",
};

/**
 * WisdomCapsulesSidePane
 * Left side library of wisdom resources. Each capsule opens in a new tab
 * preserving its original format (HTML stays HTML, YouTube stays YouTube, etc).
 *
 * - Desktop: docked left, sticky inside its column
 * - Mobile: collapsible drawer triggered by a floating "Wisdom Capsules" button
 */
const WisdomCapsulesSidePane = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* DESKTOP — side pane (parent controls positioning) */}
      <div className="hidden lg:block" data-testid="wisdom-capsules-pane">
        <PaneCard />
      </div>

      {/* MOBILE — floating trigger + drawer */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed bottom-24 left-4 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-shadow"
        data-testid="wisdom-capsules-mobile-trigger"
      >
        <BookOpen className="w-4 h-4" />
        <span className="text-sm font-semibold">Wisdom Capsules</span>
      </button>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="relative bg-card w-full max-w-md rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-muted"
              data-testid="wisdom-capsules-mobile-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <PaneCard onItemClick={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};

/* -------------------- Pane content -------------------- */
const PaneCard = ({ onItemClick }) => (
  <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
    <div className="flex items-center gap-2 pb-4 mb-4 border-b border-border">
      <BookOpen className="w-5 h-5 text-secondary" />
      <div>
        <h3 className="font-serif text-lg font-bold text-primary leading-tight">
          Wisdom Capsules
        </h3>
        <p className="text-xs text-muted-foreground">A library of teachings · click to open</p>
      </div>
    </div>

    <div className="space-y-5">
      {wisdomCapsules.map((chapter) => (
        <ChapterBlock key={chapter.number} chapter={chapter} onItemClick={onItemClick} />
      ))}
    </div>
  </div>
);

const ChapterBlock = ({ chapter, onItemClick }) => {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left mb-2"
        data-testid={`chapter-toggle-${chapter.number}`}
      >
        <span>
          <span className="block text-[11px] font-bold tracking-wider uppercase text-accent">
            Chapter {chapter.number}
          </span>
          <span className="block font-serif text-sm font-semibold text-foreground leading-snug">
            {chapter.title}
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <ul className="space-y-1.5 pl-1">
          {chapter.topics.map((topic) => (
            <li key={topic.number}>
              <CapsuleLink topic={topic} onItemClick={onItemClick} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CapsuleLink = ({ topic, onItemClick }) => {
  const Icon = TYPE_ICON[topic.type] || ExternalLink;
  const action = TYPE_LABEL[topic.type] || "Open";
  return (
    <a
      href={topic.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onItemClick}
      className="group flex items-start gap-3 px-3 py-2.5 rounded-lg border border-transparent hover:border-secondary/30 hover:bg-secondary/5 transition-colors"
      data-testid={`capsule-link-${topic.number}`}
    >
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-white transition-colors flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-foreground leading-snug">
          <span className="text-accent font-semibold mr-1">{topic.number}</span>
          {topic.title}
        </span>
        <span className="block text-[11px] text-muted-foreground mt-0.5 uppercase tracking-wider">
          {action} →
        </span>
      </span>
    </a>
  );
};

export default WisdomCapsulesSidePane;
